/**
 * Asset Shape Language
 *
 * This module codifies the asset icon shape language used throughout AsseTrac:
 *
 * CIRCLE shape → Battery-powered assets with gateways
 *   - These assets have their own power source and communicate via gateway devices
 *   - Visual: Pure circle with full radius (borderRadius: '50%')
 *
 * SQUIRCLE shape → Beacon-enabled assets (passive tracking)
 *   - Mat, Tool, Material, Waste, Trailer, and similar asset types
 *   - Visual: Rounded square with 8px border radius (squircle look)
 *
 * This shape distinction supports accessibility for colorblind users by providing
 * a visual differentiator beyond color alone.
 */

/**
 * Asset shape constants
 */
export const ASSET_SHAPE = Object.freeze({
  /** Circle shape for battery-powered/gateway assets - full radius */
  CIRCLE: 'circle',
  /** Squircle shape for beacon-enabled assets - 8px border radius */
  SQUIRCLE: 'squircle',
});

/**
 * Border radius values for each shape
 */
export const ASSET_SHAPE_BORDER_RADIUS = Object.freeze({
  [ASSET_SHAPE.CIRCLE]: '50%',
  [ASSET_SHAPE.SQUIRCLE]: '8px',
});

/**
 * MUI theme spacing unit equivalent (for borderRadius: 1 = 8px)
 */
export const ASSET_SHAPE_BORDER_RADIUS_MUI = Object.freeze({
  [ASSET_SHAPE.CIRCLE]: '50%',
  [ASSET_SHAPE.SQUIRCLE]: 1, // MUI spacing unit (8px)
});

/**
 * Determines the appropriate shape for an asset based on its type and device configuration.
 *
 * Battery-powered assets (with gateways) → CIRCLE
 * Beacon-enabled assets (mats, tools, materials, etc.) → SQUIRCLE
 *
 * @param {Object} asset - The asset object
 * @param {string} [asset.deviceType] - Device type (e.g., 'gateway', 'beacon')
 * @param {Object} [asset.type] - Asset type object
 * @param {string} [asset.type.deviceType] - Device type from type definition
 * @param {string} [asset.assetTypeName] - Asset type name as fallback
 * @returns {string} - ASSET_SHAPE.CIRCLE or ASSET_SHAPE.SQUIRCLE
 */
export function getAssetShape(asset) {
  if (!asset) {
    return ASSET_SHAPE.SQUIRCLE;
  }

  // Check device type - gateway assets use circle shape
  const deviceType = (
    asset.deviceType ||
    asset.type?.deviceType ||
    asset.deviceTypeId ||
    ''
  ).toLowerCase();

  if (deviceType === 'gateway') {
    return ASSET_SHAPE.CIRCLE;
  }

  // All other assets (beacon-enabled: mats, tools, materials, waste, trailers, etc.)
  // use the squircle shape
  return ASSET_SHAPE.SQUIRCLE;
}

/**
 * Determines the appropriate shape for an asset type definition.
 *
 * @param {Object} assetType - The asset type object
 * @param {string} [assetType.deviceType] - Device type for this asset type
 * @param {string} [assetType.name] - Name of the asset type
 * @returns {string} - ASSET_SHAPE.CIRCLE or ASSET_SHAPE.SQUIRCLE
 */
export function getAssetTypeShape(assetType) {
  if (!assetType) {
    return ASSET_SHAPE.SQUIRCLE;
  }

  const deviceType = (assetType.deviceType || '').toLowerCase();

  if (deviceType === 'gateway') {
    return ASSET_SHAPE.CIRCLE;
  }

  return ASSET_SHAPE.SQUIRCLE;
}

/**
 * Returns the CSS borderRadius value for a given asset.
 *
 * @param {Object} asset - The asset object
 * @returns {string} - CSS borderRadius value ('50%' for circle, '8px' for squircle)
 */
export function getAssetShapeBorderRadius(asset) {
  const shape = getAssetShape(asset);
  return ASSET_SHAPE_BORDER_RADIUS[shape];
}

/**
 * Returns the MUI-compatible borderRadius value for a given asset.
 * Use this with MUI's sx prop where borderRadius: 1 equals 8px.
 *
 * @param {Object} asset - The asset object
 * @returns {string|number} - MUI borderRadius value ('50%' for circle, 1 for squircle)
 */
export function getAssetShapeBorderRadiusMui(asset) {
  const shape = getAssetShape(asset);
  return ASSET_SHAPE_BORDER_RADIUS_MUI[shape];
}

/**
 * Returns the CSS borderRadius value for a given asset type.
 *
 * @param {Object} assetType - The asset type object
 * @returns {string} - CSS borderRadius value ('50%' for circle, '8px' for squircle)
 */
export function getAssetTypeShapeBorderRadius(assetType) {
  const shape = getAssetTypeShape(assetType);
  return ASSET_SHAPE_BORDER_RADIUS[shape];
}

/**
 * Returns the MUI-compatible borderRadius value for a given asset type.
 *
 * @param {Object} assetType - The asset type object
 * @returns {string|number} - MUI borderRadius value ('50%' for circle, 1 for squircle)
 */
export function getAssetTypeShapeBorderRadiusMui(assetType) {
  const shape = getAssetTypeShape(assetType);
  return ASSET_SHAPE_BORDER_RADIUS_MUI[shape];
}

/**
 * Checks if an asset should render with a circle shape.
 *
 * @param {Object} asset - The asset object
 * @returns {boolean} - True if the asset should use circle shape
 */
export function isCircleShape(asset) {
  return getAssetShape(asset) === ASSET_SHAPE.CIRCLE;
}

/**
 * Checks if an asset should render with a squircle shape.
 *
 * @param {Object} asset - The asset object
 * @returns {boolean} - True if the asset should use squircle shape
 */
export function isSquircleShape(asset) {
  return getAssetShape(asset) === ASSET_SHAPE.SQUIRCLE;
}
